import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../../../../shared/i18n/i18n";
import { formatFullDateForUiLanguage } from "../../../../shared/i18n/dateFormatting";
import { TranslationKey } from "../../../../shared/i18n/i18n";
import {
  archiveAdminSite,
  createAdminSite,
  getAdminSite,
  unarchiveAdminSite,
  updateAdminSite,
} from "../../../../shared/requests";
import {
  AdminSiteArchiveRequest,
  AdminOrganizationPermissions,
  AdminSiteCreateRequest,
  AdminSiteDetail,
  AdminSiteDraft,
  AdminSiteGetRequest,
  AdminSiteUpdateRequest,
} from "../../../types/adminEntities";
import { useAdminOrganizationContextCache } from "../AdminOrganizationContextCache";
import { useAdminLoading } from "../AdminLoadingContext";
import { AdminNavSelection } from "../AdminNavList";
import { useAdminOrganizationContext } from "../useAdminOrganizationContext";
import { AppPopup } from "../../../../shared/components/AppPopup";
import { AdminDetailActions } from "./AdminDetailActions";
import { AdminDetailSectionLayout } from "./AdminDetailSectionLayout";
import { AdminSingleDetailsTab } from "./AdminSingleDetailsTab";
import { AdminTextField } from "./AdminTextField";
import { DisabledActionReason, hasOrganizationPermission } from "../../../utils/adminPermissions";

type SiteDetailSectionProps = {
  organizationId: null | string;
  isActive: boolean;
  isDesktopNavHidden: boolean;
  isMobileViewport: boolean;
  onCloseDesktopNav: () => void;
  onEntityMutated: () => void;
  onOpenDesktopNav: () => void;
  onSelectionChange: (selection: AdminNavSelection | null) => void;
  siteId: null | string;
  t: (key: TranslationKey) => string;
};

type SiteDetailMode = "create" | "edit" | "view";

const DEFAULT_SITE_DRAFT: AdminSiteDraft = {
  description: "",
  name: "",
};

export const SiteDetailSection = ({
  organizationId,
  isActive,
  isDesktopNavHidden,
  isMobileViewport,
  onCloseDesktopNav,
  onEntityMutated,
  onOpenDesktopNav,
  onSelectionChange,
  siteId,
  t,
}: SiteDetailSectionProps) => {
  const { language } = useI18n();
  const { track } = useAdminLoading();
  const { ensureOrganizationContext } = useAdminOrganizationContextCache();
  const { organizationContext } = useAdminOrganizationContext(organizationId);
  const [organizationPermissions, setOrganizationPermissions] = useState<AdminOrganizationPermissions | null>(null);
  const [detail, setDetail] = useState<AdminSiteDetail | null>(null);
  const [draft, setDraft] = useState<AdminSiteDraft>(DEFAULT_SITE_DRAFT);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [mode, setMode] = useState<SiteDetailMode>("view");
  const [popup, setPopup] = useState<"archive" | "discard" | "unarchive" | null>(null);

  const loadDetail = useCallback(async (nextOrganizationId: string, nextSiteId: string) => {
    setErrorMessage(null);
    setIsBusy(true);
    setMode("view");
    try {
      const request: AdminSiteGetRequest = { organizationId: nextOrganizationId, siteId: nextSiteId };
      const { payload } = await track(async () => getAdminSite(request));
      setDetail(payload);
      setDraft({
        description: payload.description ?? "",
        name: payload.name ?? "",
      });
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setDetail(null);
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  }, [t, track]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!organizationId || !siteId) {
      setDetail(null);
      setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
      setDraft(DEFAULT_SITE_DRAFT);
      setErrorMessage(null);
      setIsBusy(false);
      setMode("create");
      setPopup(null);
      return;
    }
    void loadDetail(organizationId, siteId);
  }, [organizationContext?.organizationPermissions, organizationId, isActive, loadDetail, siteId]);

  useEffect(() => {
    setOrganizationPermissions(organizationContext?.organizationPermissions ?? null);
  }, [organizationContext]);

  const sitePermissionMissing = Boolean(organizationContext) && !hasOrganizationPermission(organizationPermissions, "SITE_CREATE");
  const siteQuotaReached = Boolean(organizationContext) && organizationContext.organizationUsage.activeSiteCount >= organizationContext.activeSubscription.maxActiveSites;
  const createDisabledReason: DisabledActionReason = sitePermissionMissing
    ? "permission"
    : siteQuotaReached
      ? "subscription-limit"
      : null;
  const unarchiveDisabledReason: DisabledActionReason = sitePermissionMissing
    ? "permission"
    : siteQuotaReached
      ? "subscription-limit"
      : null;
  const canCreate = organizationId !== null && hasOrganizationPermission(organizationPermissions, "SITE_CREATE") && !siteQuotaReached;
  const canUpdate = hasOrganizationPermission(organizationPermissions, "SITE_UPDATE");
  const canArchive = hasOrganizationPermission(organizationPermissions, "SITE_ARCHIVE");
  const canUnarchive = canArchive && !siteQuotaReached;
  const updateDisabledReason: DisabledActionReason = !hasOrganizationPermission(organizationPermissions, "SITE_UPDATE") ? "permission" : null;
  const archiveDisabledReason: DisabledActionReason = !hasOrganizationPermission(organizationPermissions, "SITE_ARCHIVE") ? "permission" : null;

  const handleCreate = () => {
    if (!organizationId || (mode === "create" && !canCreate) || (mode === "edit" && !canUpdate)) {
      return;
    }
    onSelectionChange({ organizationId, create: true, type: "site" });
  };

  const handleEdit = () => {
    if (!detail || !canUpdate) {
      return;
    }
    setDraft({
      description: detail.description,
      name: detail.name,
    });
    setMode("edit");
  };

  const confirmDiscard = () => {
    if (!siteId) {
      onSelectionChange(null);
      return;
    }
    setMode("view");
    if (organizationId) {
      void loadDetail(organizationId, siteId);
    }
  };

  const handleSave = async () => {
    if (!organizationId) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      if (mode === "create") {
        const request: AdminSiteCreateRequest = {
          organizationId,
          description: draft.description,
          name: draft.name,
        };
        const { payload } = await track(async () => createAdminSite(request));
        await ensureOrganizationContext(organizationId, { force: true });
        const createdId = payload.id;
        onEntityMutated();
        if (createdId) {
          onSelectionChange({ organizationId, siteId: createdId, type: "site" });
        }
      } else if (mode === "edit" && detail) {
        const request: AdminSiteUpdateRequest = {
          organizationId: detail.organizationId,
          description: draft.description,
          name: draft.name,
          siteId: detail.id,
        };
        await track(async () => updateAdminSite(request));
        await ensureOrganizationContext(organizationId, { force: true });
        setDetail({ ...detail, description: draft.description, name: draft.name });
        setMode("view");
        onEntityMutated();
      }
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmArchive = async () => {
    if (!detail || !canArchive) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminSiteArchiveRequest = { organizationId: detail.organizationId, siteId: detail.id };
      await track(async () => archiveAdminSite(request));
      await ensureOrganizationContext(detail.organizationId, { force: true });
      if (organizationId && siteId) {
        await loadDetail(organizationId, siteId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.archiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmUnarchive = async () => {
    if (!detail || !canUnarchive) {
      return;
    }
    setErrorMessage(null);
    setIsBusy(true);
    try {
      const request: AdminSiteArchiveRequest = { organizationId: detail.organizationId, siteId: detail.id };
      await track(async () => unarchiveAdminSite(request));
      await ensureOrganizationContext(detail.organizationId, { force: true });
      if (organizationId && siteId) {
        await loadDetail(organizationId, siteId);
      }
      onEntityMutated();
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.unarchiveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const handlePopupConfirm = async () => {
    const nextPopup = popup;
    setPopup(null);
    if (nextPopup === "discard") {
      confirmDiscard();
    } else if (nextPopup === "archive") {
      await confirmArchive();
    } else if (nextPopup === "unarchive") {
      await confirmUnarchive();
    }
  };

  const popupTitle = popup === "discard"
    ? t("admin.detail.confirm.discardTitle")
    : popup === "archive"
      ? t("admin.detail.confirm.archiveTitle")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchiveTitle")
        : "";

  const popupBody = popup === "discard"
    ? t("admin.detail.confirm.discard")
    : popup === "archive"
      ? t("admin.detail.confirm.archive")
      : popup === "unarchive"
        ? t("admin.detail.confirm.unarchive")
        : "";

  const isReadOnly = mode === "view" || isBusy || (mode === "create" && !canCreate) || (mode === "edit" && !canUpdate);
  const shouldRenderPane = mode === "create" || detail !== null || errorMessage !== null;

  if (!shouldRenderPane) {
    return null;
  }

  return (
    <>
      <AdminDetailSectionLayout
        actions={detail ? (
          <AdminDetailActions
            archiveDisabledReason={archiveDisabledReason}
            canArchive={canArchive}
            canUnarchive={canUnarchive}
            canCreate={canCreate}
            createActionLabel={t("admin.detail.action.createSite")}
            canUpdate={canUpdate}
            createDisabledReason={createDisabledReason}
            hasDetail={detail !== null}
            disableNonEditingActions={mode === "create" || mode === "edit"}
            isArchived={Boolean(detail?.archivedAt)}
            isBusy={isBusy}
            isDesktopNavHidden={isDesktopNavHidden}
            isEditing={false}
            isMobileViewport={isMobileViewport}
            onArchive={() => setPopup("archive")}
            onCloseDesktopNav={onCloseDesktopNav}
            onCreate={handleCreate}
            onDiscard={() => setPopup("discard")}
            onEdit={handleEdit}
            onOpenDesktopNav={onOpenDesktopNav}
            onSave={() => void handleSave()}
            showEditAction={false}
            onUnarchive={() => setPopup("unarchive")}
            updateDisabledReason={updateDisabledReason}
            t={t}
          />
        ) : null}
        errorMessage={errorMessage}
        eyebrow={t("admin.detail.eyebrow")}
        title={t("admin.detail.title.site")}
      >
        <AdminSingleDetailsTab
          ariaLabel={t("admin.site.tabs.title")}
          panelLabel={t("admin.site.tab.details")}
          tabIdPrefix="admin-site"
        >
          <div className="admin-profile-section-header">
            <div className="admin-detail-actions admin-profile-section-actions">
              <AdminDetailActions
                canCreate={canCreate}
                canUpdate={mode === "create" ? canCreate : canUpdate}
                createDisabledReason={createDisabledReason}
                hasDetail={detail !== null}
                isArchived={Boolean(detail?.archivedAt)}
                isBusy={isBusy}
                isDesktopNavHidden={isDesktopNavHidden}
                isEditing={mode === "create" || mode === "edit"}
                isMobileViewport={isMobileViewport}
                onArchive={() => {}}
                onCloseDesktopNav={onCloseDesktopNav}
                onCreate={handleCreate}
                onDiscard={() => setPopup("discard")}
                onEdit={handleEdit}
                onOpenDesktopNav={onOpenDesktopNav}
                onSave={() => void handleSave()}
                onUnarchive={() => {}}
                showArchiveActions={false}
                showCreateAction={false}
                showNavToggle={false}
                updateDisabledReason={mode === "create" ? createDisabledReason : updateDisabledReason}
                t={t}
              />
            </div>
          </div>
          {detail || mode === "create" ? (
            <form className="admin-detail-form">
              <AdminTextField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.name")}
                onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, name: value }))}
                t={t}
                value={draft.name}
                visibility="public"
              />
              <AdminTextField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.description")}
                onChange={isReadOnly ? undefined : (value) => setDraft((current) => ({ ...current, description: value }))}
                t={t}
                value={draft.description}
                visibility="internal"
              />
              {detail?.archivedAt ? (
                <AdminTextField
                  isReadOnly
                  label={t("admin.detail.field.archivedAt")}
                  t={t}
                  value={formatFullDateForUiLanguage(detail.archivedAt, language)}
                  visibility="internal"
                />
              ) : null}
            </form>
          ) : null}
        </AdminSingleDetailsTab>
      </AdminDetailSectionLayout>
      <AppPopup
        actions={(
          <>
            <button
              className="admin-popup-action"
              disabled={isBusy}
              onClick={() => setPopup(null)}
              type="button"
            >
              {t("admin.popup.cancel")}
            </button>
            <button
              className="admin-popup-action primary"
              disabled={isBusy}
              onClick={() => void handlePopupConfirm()}
              type="button"
            >
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup !== null}
        onClose={() => setPopup(null)}
        title={popupTitle}
      >
        <p>{popupBody}</p>
      </AppPopup>
    </>
  );
};
